import React, { PropsWithChildren } from 'react';
import { styled } from '@styledComponents';
import { CTALink } from '@components/CTALink';
import { themeColor, themeVariant, themeMediaQuery } from '@utils/styledTheme.util';
import { dottedLinkStyles } from './DottedLink';
import { JoykitIcon } from '@components/JoykitIcon';

/**
 * ChevronLink - A reusable "Learn More" style link with:
 * - Dotted underline (solid on hover)
 * - Forward chevron icon (JoykitIcon forwardChevron)
 * - featureLink20 typography (20px, 500 weight)
 * - Dark grey color (mono1) with purple hover (actionPurple3)
 * - 8px gap between text and icon
 *
 * Use for "Learn More", "Explore", and similar secondary CTA links.
 * This is a global reusable component - import from '@components/Link'.
 *
 * @example
 * import { ChevronLink } from '@components/Link';
 *
 * <ChevronLink id="learnMore" href="/page/">
 *   Learn More
 * </ChevronLink>
 *
 * // With custom styling wrapper:
 * const StyledLink = styled(ChevronLink)`
 *   margin-top: ${themeSpace(7)};
 * `;
 */

const StyledCTALink = styled(CTALink).attrs({
  typography: 'featureLink20',
  hasChevron: false,
  hasSpan: false,
})`
  ${dottedLinkStyles}
  ${themeVariant('featureCopy17')}
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: ${themeColor('mono1')};
  font-weight: 500;

  &:hover {
    color: ${themeColor('actionPurple3')};
  }

  /* lg and xl breakpoints use 20px */
  ${themeMediaQuery('lg')} {
    ${themeVariant('featureLink20')}
  }

  /* JoykitIcon wrapper */
  > div {
    display: inline-flex;
    align-items: center;
    width: 16px;
    height: 16px;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

interface ChevronLinkProps {
  id: string;
  href: string;
  className?: string;
}

export const ChevronLink: React.FC<PropsWithChildren<ChevronLinkProps>> = ({ id, href, className, children }) => (
  <StyledCTALink id={id} href={href} className={className}>
    <span>{children}</span>
    <JoykitIcon name="forwardChevron" size={16} isInline />
  </StyledCTALink>
);
